import { Progress } from '@eleveo/components-react';
import KeycloakJS, { KeycloakError, KeycloakInstance } from 'keycloak-js';
import React, { useEffect, useState } from 'react';

import { useConfig } from './ConfigurationProvider';
import { ExternalRedirect } from '../router/ExternalRedirect';
import i18n, { DEFAULT_LOCALE } from '../translations/i18n';
import { supportedLocales } from '../translations/resources';
import { getTimeZone } from '../utils/dateUtils';
import {
  Authorization,
  getSessionTimeoutFromRefreshToken,
  keycloakUserHasPermissions,
  Pending,
} from '../utils/keycloakUtils';
import { authenticatedStateMock } from '../utils/test/authorizationMock';

export const AuthorizationContext = React.createContext(authenticatedStateMock);

const initialState: Pending = {
  status: 'pending',
};

export const AuthorizationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { appUrl, authUrl, clientId, tenant } = useConfig();

  const [auth, setAuth] = useState<Authorization>(initialState);

  useEffect(() => {
    const keycloak: KeycloakInstance = KeycloakJS({
      clientId,
      realm: tenant,
      url: authUrl,
    });

    keycloak.onReady = (authenticated?: boolean) => {
      if (authenticated === false) {
        setAuth({
          status: 'non-authenticated',
          redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
        });
      }
    };

    keycloak.onAuthSuccess = () => {
      // `tokenParsed` contains user data that are not defined in types
      const {
        tokenParsed: {
          locale = DEFAULT_LOCALE, // fallback to default language
          name: fullUserName,
          preferred_username: userName,
          sub,
          timezone: { zoneId } = {} as any, // Default value as quick-fix to work with older KC version
        } = {} as any,
      } = keycloak;

      const userLocale: string = supportedLocales.includes(locale) ? locale : DEFAULT_LOCALE;

      i18n.changeLanguage(userLocale);

      const logoutUrl = new URL(keycloak.createLogoutUrl({ redirectUri: appUrl }));

      setAuth({
        keycloak,
        status: 'authenticated',
        logout: () => {
          window.location.replace(logoutUrl);
        },
        logoutUrl: logoutUrl.toString(),
        forceUpdateToken: () => {
          /**
           * According to implementation details of KC library `-1` is used to force refresh and log appropriate message
           * if logging in KC library is enabled.
           *
           * minValidity is in seconds, and by default its set to 5s, therefore it is not sufficient to use 0s here.
           */
          keycloak.updateToken(-1);
        },
        manageAccountUrl: keycloak.createAccountUrl(),
        userHasPermissions: keycloakUserHasPermissions(keycloak),
        userId: sub,
        // using userName (preferred_username) as a fallback value considering it is a required field in KC
        userName: fullUserName || userName,
        // use browser's timezone in case of user has not defined timezone in his user account
        timezone: zoneId ?? getTimeZone(),
        sessionIdleTimeDuration: getSessionTimeoutFromRefreshToken(keycloak.refreshTokenParsed),
        // get locale from KC or browser preferred language as fallback
        locale: userLocale,
      });
    };

    keycloak.onAuthError = (errorData: KeycloakError) => console.error(`Authorization: ${JSON.stringify(errorData)}`);

    keycloak.onAuthLogout = () => {
      setAuth({
        status: 'non-authenticated',
        redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
      });
    };

    keycloak.onAuthRefreshError = () => {
      setAuth({
        status: 'non-authenticated',
        redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
      });
    };

    keycloak.onAuthRefreshSuccess = () => {
      setAuth((prev) => {
        if (prev.status === 'authenticated') {
          const sessionTimeout = getSessionTimeoutFromRefreshToken(prev.keycloak.refreshTokenParsed);
          return {
            ...prev,
            sessionIdleTimeDuration: sessionTimeout,
          };
        }

        if (prev.status === 'pending') {
          return prev;
        }

        return {
          status: 'non-authenticated',
          redirectUrl: keycloak.createLoginUrl({ redirectUri: window.location.href }),
        };
      });
      console.debug('Authorization: Access token successfully refreshed.');
    };

    keycloak.onTokenExpired = () => {
      console.debug('Authorization: Access token expired, updating...');
      keycloak.updateToken(9999);
    };

    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${appUrl}/silent-check-sso.html`,
      })
      .then((isAuthenticated) => {
        if (isAuthenticated) {
          console.debug('Keycloak init was successful and user is authenticated.');
          return;
        }
        console.warn('Keycloak init was successful, but user is not authenticated. Redirecting to login...');
        keycloak.login();
      })
      .catch(() => {
        console.warn('Keycloak init failed! Redirecting to login...');
        keycloak.login();
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (auth.status === 'pending') {
    return <Progress withOverlay />;
  }

  if (auth.status === 'non-authenticated') {
    return <ExternalRedirect to={auth.redirectUrl} />;
  }

  return <AuthorizationContext.Provider value={auth}>{children}</AuthorizationContext.Provider>;
};

export const AuthorizationConsumer = AuthorizationContext.Consumer;

export const useAuth = () => React.useContext(AuthorizationContext);
